import React, { useState, useEffect, useRef } from 'react';
import { getCallbackRequests, updateCallbackRequest } from '../../services/clientService';
import styles from './CallbackRequests.module.css';
import { Copy, Download, Calendar } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const CallbackRequests = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [filterType, setFilterType] = useState('all'); // 'all', 'today', 'week', 'month', 'custom'
  const [customDate, setCustomDate] = useState('');
  const [loading, setLoading] = useState(true);
  const pdfRef = useRef(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    const data = await getCallbackRequests();
    setRequests(data);
    setFilteredRequests(data);
    setLoading(false);
  };

  useEffect(() => {
    applyFilter();
  }, [filterType, customDate, requests]);

  const applyFilter = () => {
    if (filterType === 'all') {
      setFilteredRequests(requests);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const filtered = requests.filter(req => {
      const vDate = new Date(req.createdAt);
      if (filterType === 'today') {
        const d = new Date(vDate);
        d.setHours(0,0,0,0);
        return d.getTime() === today.getTime();
      }
      if (filterType === 'week') {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return vDate >= startOfWeek;
      }
      if (filterType === 'month') {
        return vDate.getMonth() === today.getMonth() && vDate.getFullYear() === today.getFullYear();
      }
      if (filterType === 'custom' && customDate) {
        const cDate = new Date(customDate);
        cDate.setHours(0,0,0,0);
        const d = new Date(vDate);
        d.setHours(0,0,0,0);
        return d.getTime() === cDate.getTime();
      }
      return true;
    });

    setFilteredRequests(filtered);
  };

  const copyMobile = (number) => {
    navigator.clipboard.writeText(number);
  };

  const downloadPDF = async () => {
    const element = pdfRef.current;
    if (!element) return;
    
    try {
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Callback_Requests_${filterType}.pdf`);
    } catch (error) {
      console.error("PDF generation failed", error);
    }
  };

  const updateStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'New' ? 'Contacted' : 'New';
    await updateCallbackRequest(id, { status: newStatus });
    fetchRequests();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Callback Requests</h2>
        <div className={styles.actions}>
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className={styles.select}
          >
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="custom">Date Wise</option>
          </select>
          {filterType === 'custom' && (
            <input 
              type="date" 
              value={customDate} 
              onChange={(e) => setCustomDate(e.target.value)}
              className={styles.dateInput}
            />
          )}
          <button className={styles.btnAction} onClick={downloadPDF}>
            <Download size={16} /> Print PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading data...</div>
      ) : (
        <div className={styles.tableWrapper} ref={pdfRef}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Mobile Number</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.length > 0 ? (
                filteredRequests.map(req => (
                  <tr key={req.id}>
                    <td>{new Date(req.createdAt).toLocaleDateString()}</td>
                    <td>{req.name || 'N/A'}</td>
                    <td>
                      <div className={styles.mobileContainer}>
                        {req.mobile || 'N/A'}
                        {req.mobile && (
                          <button 
                            className={styles.copyBtn} 
                            onClick={() => copyMobile(req.mobile)}
                            title="Copy number"
                          >
                            <Copy size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={req.status === 'Contacted' ? styles.statusContacted : styles.statusNew}>
                        {req.status || 'New'}
                      </span>
                    </td>
                    <td>
                      <button 
                        className={styles.statusToggleBtn} 
                        onClick={() => updateStatus(req.id, req.status || 'New')}
                      >
                        Mark as {req.status === 'Contacted' ? 'New' : 'Contacted'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className={styles.noData}>No callback requests found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CallbackRequests;
